import { useEffect, useRef } from 'react'
import type { ChatMessage, Participant } from '@shared/protocol'
import { tocarSom, type SomId } from '@/lib/sounds'

/**
 * Os avisos sonoros da sala, tirados da diferenca entre dois instantes.
 *
 * Nao ha evento no protocolo pra "fulano entrou na voz": o servidor manda o
 * participante inteiro num `peer-update` e o cliente compara com o que tinha
 * antes. Fazer a comparacao aqui, num lugar so, evita espalhar `tocarSom` por
 * dentro do `useRoomSession` — e deixa a regra testavel sem Web Audio nenhum.
 */

/** O que interessa de uma pessoa pra decidir se algum som deve tocar. */
export interface EstadoPessoa {
    voz: boolean
    /** Quantas transmissoes de imagem (tela e/ou camera) ela tem no ar. */
    videos: number
}

export type Instantaneo = Map<string, EstadoPessoa>

export function instantaneoDe(participants: Participant[]): Instantaneo {
    const instantaneo: Instantaneo = new Map()
    for (const pessoa of participants) {
        instantaneo.set(pessoa.id, {
            voz: pessoa.publications.some((publication) => publication.kind === 'voice'),
            videos: pessoa.publications.filter((publication) => publication.kind !== 'voice').length
        })
    }
    return instantaneo
}

const VAZIO: EstadoPessoa = { voz: false, videos: 0 }

/**
 * Quais sons a mudanca de um instante pro outro pede.
 *
 * Conta transmissoes em vez de olhar tipo por tipo porque trocar a tela pela
 * camera e uma acao so: com a comparacao por tipo, ela soaria como um fim e um
 * comeco colados, que e exatamente o barulho que ninguem quer ouvir.
 *
 * Quem sai da sala conta como quem desligou tudo — some da lista, e o que
 * estava no ar deixou de estar.
 */
export function sonsDaMudanca(antes: Instantaneo, depois: Instantaneo): SomId[] {
    const sons: SomId[] = []
    const ids = new Set([...antes.keys(), ...depois.keys()])

    for (const id of ids) {
        const de = antes.get(id) ?? VAZIO
        const para = depois.get(id) ?? VAZIO

        if (!de.voz && para.voz) sons.push('voz-entrou')
        if (de.voz && !para.voz) sons.push('voz-saiu')
        if (para.videos > de.videos) sons.push('transmissao-comecou')
        if (para.videos < de.videos) sons.push('transmissao-parou')
    }

    // Tres pessoas entrando na voz de uma vez sao um aviso, nao tres: o
    // `peer-update` de cada uma chega em rajada quando a sala esta comecando.
    return [...new Set(sons)]
}

/**
 * Toca os avisos da sala.
 *
 * `silencioso` desliga tudo — e por onde o "silenciar a sala" chega aqui. Quem
 * pediu pra nao ouvir a sala nao pediu pra ouvir menos: pediu pra nao ouvir.
 */
export function useRoomSounds(
    /** Quem esta na mesma sub-sala que voce — so o que se ouve se anuncia. */
    participants: Participant[],
    messages: ChatMessage[],
    /** `null` enquanto a sala ainda nao confirmou quem voce e. */
    selfId: string | null,
    silencioso: boolean,
    /** Sub-sala em que voce esta (`null` = sala principal). */
    canal: string | null = null,
    /** Muda toda vez que a conversa e trocada inteira por um historico. */
    versaoDoHistorico = 0
): void {
    /**
     * `null` ate a primeira passada.
     *
     * Sem isso, entrar numa sala em que cinco pessoas ja transmitem tocaria os
     * cinco avisos de uma vez: pro codigo, todas elas "acabaram de comecar".
     */
    const anterior = useRef<Instantaneo | null>(null)
    const canalAnterior = useRef(canal)
    const silenciosoRef = useRef(silencioso)
    silenciosoRef.current = silencioso

    useEffect(() => {
        const agora = instantaneoDe(participants)
        const antes = anterior.current
        anterior.current = agora
        /**
         * Trocar de sub-sala e como entrar numa sala nova: a lista inteira
         * muda de uma vez, e comparar com a anterior tocaria "entrou" pra
         * cada pessoa de la e "saiu" pra cada pessoa de ca — tudo junto.
         */
        const trocouDeCanal = canalAnterior.current !== canal
        canalAnterior.current = canal
        if (!antes || trocouDeCanal || silenciosoRef.current) return

        for (const som of sonsDaMudanca(antes, agora)) tocarSom(som)
    }, [participants, canal])

    /**
     * Quantas mensagens ja tinham passado por aqui.
     *
     * O aviso e por mensagem NOVA, e o teste disso e a lista ter crescido em
     * exatamente uma desde a ultima vez — dentro da MESMA conversa. O
     * historico que chega ao entrar na sala (ou ao trocar de sub-sala) vem em
     * bloco e muda a versao abaixo: a conversa que aconteceu antes de voce
     * chegar nao se anuncia, nem quando ela tem uma mensagem so.
     */
    const totalConhecido = useRef<number | null>(null)
    /**
     * Quantas vezes a conversa foi trocada inteira por um historico.
     *
     * Trocar de sub-sala traz a conversa de la num bloco so, e pelo tamanho
     * nao da pra separar isso de uma mensagem nova: o historico de uma
     * sub-sala pode ter exatamente uma a mais que a conversa de onde a pessoa
     * saiu. A versao muda junto com a lista, no mesmo render, entao nao ha
     * adivinhacao.
     */
    const versaoConhecida = useRef(versaoDoHistorico)

    useEffect(() => {
        const total = messages.length
        const antes = versaoConhecida.current === versaoDoHistorico ? totalConhecido.current : null
        versaoConhecida.current = versaoDoHistorico
        totalConhecido.current = total
        if (antes === null || total !== antes + 1) return

        const ultima = messages[total - 1]
        if (!ultima) return
        // O proprio texto nao se anuncia: quem escreveu ja sabe que escreveu.
        if (ultima.from === selfId) return
        if (silenciosoRef.current) return

        tocarSom('mensagem')
    }, [messages, selfId, versaoDoHistorico])
}
